import { Box, Text } from '@user-portal/wikr-components';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

import { selectCurrentUser } from 'store/user/selectors';
import { selectCancellationCandidates } from 'store/subscriptions/selectors';

import { DISCOUNT_TYPES } from 'constants/subscriptions';
import { CANCEL_SUBSCRIPTION_SCREEN_ID } from 'constants/analytics';

import { trackScreenLoad } from 'services/Analytics/trackers/mainTrackers';
import { sendAnalyticCancelOfferClick } from 'services/Analytics';

import useModals from 'modals/hooks';
import { useSelector } from 'hooks';
import { useGetTexts } from '../../hooks/useGetTexts';
import useCancelSubscriptionFlow from '../../../hooks/useCancelSubscriptionFlow';
import { checkDiscountAllowed } from '../../../hooks/helpers';

import { getValuesByKey } from 'helpers/utils';
import { isMainSubscription } from 'helpers/subscriptions';

import Modal from 'components/Modal/Modal';
import OutlinedButton from 'components/Buttons/OutlinedButton';
import CustomPrimaryButton from 'components/Buttons/CustomPrimaryButton';
import Card from './subComponents/Card';
import HeaderWithLogo from '../../components/HeaderWithLogo';
import AutoSlider from '../../components/AutoSlider';

import { CancelOfferAction } from 'types/subscription';

import { getSliderConfig } from './config';

const ConfirmationModalWithDiscountA = () => {
    const { t } = useTranslation();

    const { closeModal } = useModals();

    const { country } = useSelector(selectCurrentUser);

    const { onCancelSubscriptionWithOffer, onCancelSubscription } = useCancelSubscriptionFlow();

    const cancellationCandidates = useSelector(selectCancellationCandidates);
    const subscription_id = getValuesByKey(cancellationCandidates, 'external_id');

    const sliderConfig = getSliderConfig();

    const isMultiSubs = cancellationCandidates && cancellationCandidates?.length > 1;

    const { title, subtitle } = useGetTexts({
        titleText: isMultiSubs
            ? 'subscription.confirmationModalWithDiscount.title.multi'
            : 'subscription.confirmationModalWithDiscount.title.single',
        subtitleText: 'subscription.confirmationModalWithDiscountA.title.single',
    });

    useEffect(() => {
        if (subscription_id) {
            trackScreenLoad(CANCEL_SUBSCRIPTION_SCREEN_ID, {
                subscription_id,
            });
        }
    }, [subscription_id]);

    const onSubmit = () => {
        sendAnalyticCancelOfferClick({
            screenId: CANCEL_SUBSCRIPTION_SCREEN_ID,
            actionEvent: CancelOfferAction.Cancel,
            eventLabel: { subscription_id },
        });

        if (cancellationCandidates?.[0]) {
            const {
                product_type,
                discount: { discount_type },
            } = cancellationCandidates[0];

            isMainSubscription(product_type) && discount_type !== DISCOUNT_TYPES.extra && checkDiscountAllowed(country)
                ? onCancelSubscriptionWithOffer()
                : onCancelSubscription();
        }
    };

    const onBackButtonClick = () => {
        sendAnalyticCancelOfferClick({
            screenId: CANCEL_SUBSCRIPTION_SCREEN_ID,
            actionEvent: CancelOfferAction.Close,
            eventLabel: { subscription_id },
        });

        closeModal();
    };

    return (
        <Modal
            fullscreen
            isOpen
            customStyles={{ padding: 0 }}
            withRoundedCorners={false}
            dataTestId="confirmationModalWithDiscountA"
        >
            <HeaderWithLogo onBackButtonClick={onBackButtonClick} />

            <Box className="container-sm" $paddingTop={24}>
                <Text medium mb={12} type="h5">
                    {t(title)}
                </Text>
                <Text mb={24}>{t(subtitle)}</Text>
                <AutoSlider>
                    {sliderConfig.map(({ img, title, description }) => (
                        <Card img={img} title={title} description={description} key={title} />
                    ))}
                </AutoSlider>

                <CustomPrimaryButton onClick={onBackButtonClick} dataLocator="getOneMonthButton" mb={16}>
                    {t('subscription.cancelDialog.discard')}
                </CustomPrimaryButton>
                <OutlinedButton
                    onClick={onSubmit}
                    text={t('basics.button.cancelSubscription')}
                    backgroundColor="surface-main"
                    dataLocator="cancelSubscriptionButton"
                    mb={32}
                />
            </Box>
        </Modal>
    );
};

export default ConfirmationModalWithDiscountA;
